import { useCallback, useEffect, useState } from "react";
export const useRestoreFocusOnRemove = ({ hasValues, isInline, lastFocusedIndex, popperProps, shouldFocusOnMount, toggleRef, toggleWrapperRef }) => {
    const [isOpen, setIsOpen] = useState(popperProps?.isOpen ?? false);
    if (popperProps?.isOpen !== undefined && isOpen !== popperProps?.isOpen) {
        setIsOpen(popperProps?.isOpen);
    }
    useEffect(() => {
        if (shouldFocusOnMount && !isInline && !isOpen && !toggleWrapperRef.current?.contains(document.activeElement)) {
            let focusTarget;
            if (hasValues && lastFocusedIndex.current !== -1) {
                const selectedValues = toggleWrapperRef.current?.querySelectorAll('[role="listitem"]');
                if (selectedValues === undefined) {
                    // this should never happen if renderSelectedMultiOption is implemented correctly
                    // eslint-disable-next-line no-console -- warning about incorrect API usage
                    console.warn(`[ALCHEMY] <MultiSelect> is trying to restore focus to a selected value's remove button but none can be found, check your renderSelectedMultiOption implementation`, toggleWrapperRef.current);
                    return;
                }
                const trySetTarget = (i) => {
                    if (focusTarget !== undefined) {
                        return;
                    }
                    const listItem = selectedValues[i];
                    const button = listItem?.querySelector("button");
                    if (button) {
                        focusTarget = button;
                    }
                };
                // first, try to restore focus to the previous item
                if (lastFocusedIndex.current !== 0) {
                    trySetTarget(lastFocusedIndex.current - 1);
                }
                // If possible, restore to the item with the same index, otherwise restore to the last item before it that has a remove button
                const indexToFocus = selectedValues.length > lastFocusedIndex.current ? lastFocusedIndex.current : selectedValues.length - 1;
                // eslint-disable-next-line no-unmodified-loop-condition -- trySetTarget will set focusTarget if it finds a button
                for (let i = indexToFocus; focusTarget === undefined && i >= 0; i--) {
                    trySetTarget(i);
                }
                // Finally, look for the first item after the one we're trying to restore focus to that has a remove button
                // eslint-disable-next-line no-unmodified-loop-condition -- trySetTarget will set focusTarget if it finds a button
                for (let i = indexToFocus + 1; focusTarget === undefined && i < selectedValues.length; i++) {
                    trySetTarget(i);
                }
            }
            else {
                focusTarget = toggleRef.current;
            }
            const timeout = setTimeout(() => {
                focusTarget?.focus({ preventScroll: true });
            }, 0);
            return () => clearTimeout(timeout);
        }
        return () => { };
    });
    const handlePopperOpen = useCallback(() => {
        popperProps?.onOpen?.();
        setIsOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- React is wrong
    }, [popperProps?.onOpen]);
    const handlePopperClose = useCallback(() => {
        popperProps?.onClose?.();
        setIsOpen(false);
        lastFocusedIndex.current = -1;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- React is wrong
    }, [popperProps?.onClose]);
    return {
        handlePopperClose,
        handlePopperOpen
    };
};
